import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Badge,
  Spinner,
  Alert,
  Form,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { getPengajuanPendampingList } from "../../services/api";
import { resolvePendampingId } from "../../utils/pendampingSession";
import "./Dashboard.css";

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const _FILTER_KEY = "_laporan_sh_terbit_state";

const LaporanSHTerbit = () => {
  const navigate = useNavigate();

  // Restore saved filter state
  const _saved = (() => {
    try {
      const raw = sessionStorage.getItem(_FILTER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  // State management
  const [pengajuanData, setPengajuanData] = useState(_saved?.pengajuanData || []);
  const [loadingPengajuan, setLoadingPengajuan] = useState(false);
  const [pengajuanError, setPengajuanError] = useState(null);
  const [totalPengajuan, setTotalPengajuan] = useState(_saved?.totalPengajuan || 0);
  const [tableSearch, setTableSearch] = useState(_saved?.tableSearch || "");

  // Clear saved filter state once restored
  useEffect(() => {
    sessionStorage.removeItem(_FILTER_KEY);
  }, []);

  // Load pengajuan data on mount
  useEffect(() => {
    const load = async () => {
      const id = await resolvePendampingId();
      if (!id) {
        setPengajuanError("ID Pendamping tidak ditemukan. Silakan login kembali.");
        return;
      }
      fetchPengajuan(id);
    };
    load();
  }, []);

  const fetchPengajuan = async (idPendamping) => {
    try {
      setLoadingPengajuan(true);
      setPengajuanError(null);
      setPengajuanData([]);

      const res = await getPengajuanPendampingList(idPendamping);
      
      if (res.data.success) {
        // Filter hanya status "Terbit SH"
        const allData = res.data.data || [];
        const terbitShData = allData.filter(item => item.status_desc === "Terbit SH");
        
        setPengajuanData(terbitShData);
        setTotalPengajuan(terbitShData.length);
      } else {
        setPengajuanError(res.data.message || "Gagal memuat data pengajuan");
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      setPengajuanError(msg || "Terjadi kesalahan saat memuat data");
    } finally {
      setLoadingPengajuan(false);
    }
  };

  const filteredPengajuan = pengajuanData.filter((row) => {
    if (!tableSearch) return true;
    const q = tableSearch.toLowerCase();
    return (
      (row.no_daftar || "").toLowerCase().includes(q) ||
      (row.nama_pu || "").toLowerCase().includes(q) ||
      (row.merek_dagang || "").toLowerCase().includes(q) ||
      (row.prov_pu || "").toLowerCase().includes(q) ||
      (row.kota_pu || "").toLowerCase().includes(q) ||
      (row.status_desc || "").toLowerCase().includes(q) ||
      (row.jenis_produk_desc || "").toLowerCase().includes(q) ||
      (row.email || "").toLowerCase().includes(q) ||
      (row.no_telp || "").toLowerCase().includes(q)
    );
  });

  const getStatusBadge = (status) => {
    if (!status) return <Badge bg="secondary">-</Badge>;
    const s = status.toLowerCase();
    if (s.includes("terbit") || s.includes("selesai"))
      return <Badge bg="success">{status}</Badge>;
    if (s.includes("proses") || s.includes("verif"))
      return <Badge bg="primary">{status}</Badge>;
    if (s.includes("tolak") || s.includes("batal"))
      return <Badge bg="danger">{status}</Badge>;
    if (s.includes("kirim") || s.includes("dokumen"))
      return (
        <Badge bg="warning" text="dark">
          {status}
        </Badge>
      );
    if (s.includes("submit"))
      return <Badge bg="info">{status}</Badge>;
    if (s.includes("draft"))
      return <Badge bg="secondary">{status}</Badge>;
    return <Badge bg="info">{status}</Badge>;
  };

  return (
    <div>
      <div
        className="page-header-banner"
        style={{
          background:
            "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 50%, var(--primary-dark) 100%)",
        }}
      >
        <div className="page-header-content">
          <div className="page-header-left">
            <div className="page-header-icon">
              <i className="fas fa-certificate"></i>
            </div>
            <div>
              <h1 className="page-header-title">Laporan SH Terbit</h1>
              <p className="page-header-subtitle">
                Laporan sertifikat halal yang telah terbit
              </p>
            </div>
          </div>
        </div>
      </div>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <span className="fw-semibold">
            <i className="fas fa-list me-2"></i>
            Sertifikat Halal Terbit
            {totalPengajuan > 0 && (
              <Badge bg="success" className="ms-2">
                {totalPengajuan}
              </Badge>
            )}
          </span>
          <Form.Control
            type="text"
            size="sm"
            placeholder="Cari no. daftar, nama PU, merek, email..."
            style={{ maxWidth: 320 }}
            value={tableSearch}
            onChange={(e) => setTableSearch(e.target.value)}
          />
        </Card.Header>
        <Card.Body className="p-0">
          {pengajuanError && (
            <Alert variant="danger" className="m-3">
              <i className="fas fa-exclamation-circle me-2"></i>
              {pengajuanError}
            </Alert>
          )}
          
          {loadingPengajuan ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Memuat data pengajuan...</p>
            </div>
          ) : pengajuanData.length === 0 && !pengajuanError ? (
            <div className="text-center py-5 text-muted">
              <i className="fas fa-inbox fa-2x mb-2 d-block"></i>
              <p className="mb-0">Belum ada sertifikat halal yang terbit</p>
            </div>
          ) : filteredPengajuan.length === 0 && tableSearch ? (
            <div className="text-center py-5 text-muted">
              <i className="fas fa-search fa-2x mb-2 d-block"></i>
              <p className="mb-0">Tidak ada data yang cocok dengan pencarian</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0" style={{ fontSize: "0.85rem" }}>
                  <thead className="table-light">
                    <tr>
                      <th className="px-3">#</th>
                      <th>No. Daftar</th>
                      <th>Tgl. Daftar</th>
                      <th>Nama PU</th>
                      <th>Merek Dagang</th>
                      <th>Alamat</th>
                      <th>Kota/Kab</th>
                      <th>Jenis Produk</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPengajuan.map((row, idx) => (
                      <tr key={row.id_reg || idx}>
                        <td className="px-3 text-muted">{idx + 1}</td>
                        <td className="fw-semibold text-nowrap">
                          {row.no_daftar || row.no_mohon || "-"}
                        </td>
                        <td className="text-nowrap">
                          {formatDate(row.tgl_daftar || row.tgl_mohon)}
                        </td>
                        <td>{row.nama_pu || "-"}</td>
                        <td>{row.merek_dagang || "-"}</td>
                        <td style={{ maxWidth: 200 }}>
                          {row.alamat_pu || "-"}
                        </td>
                        <td>{row.kota_pu || "-"}</td>
                        <td style={{ maxWidth: 220 }}>
                          {row.jenis_produk_desc || "-"}
                        </td>
                        <td>{row.email || "-"}</td>
                        <td className="text-nowrap">
                          {getStatusBadge(row.status_desc)}
                        </td>
                        <td className="text-nowrap">
                          {row.id_reg && (
                            <button
                              className="btn btn-outline-primary btn-sm py-0 px-2"
                              style={{ fontSize: "0.75rem" }}
                              onClick={() => {
                                sessionStorage.setItem(
                                  _FILTER_KEY,
                                  JSON.stringify({
                                    pengajuanData,
                                    totalPengajuan,
                                    tableSearch,
                                  }),
                                );
                                const token = Math.random()
                                  .toString(36)
                                  .slice(2, 13);
                                localStorage.setItem(
                                  `_dreg_${token}`,
                                  row.id_reg,
                                );
                                navigate(
                                  `/admin/pendamping/detail-registrasi/${token}`,
                                );
                              }}
                            >
                              <i className="fas fa-external-link-alt me-1"></i>
                              Detil
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              {filteredPengajuan.length > 0 && (
                <div className="px-3 py-2 text-muted small border-top">
                  Menampilkan {filteredPengajuan.length} dari{" "}
                  {pengajuanData.length} data
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default LaporanSHTerbit;
